/**
 * Country and city reference data for the CRM address fields.
 *
 * The API stores country and city as plain strings and existing rows hold typed
 * names, so what we save is the country's English short name ("United Arab
 * Emirates"), never the ISO code. The code is kept only to pick out the common
 * markets for the top of the dropdown.
 *
 * City lists are curated for the markets this Dubai-based business sells into:
 * the main cities, spelled the usual English way, alphabetical. A country
 * without a list gets a free-text city field in the form.
 */

export type Country = {
  /** ISO 3166-1 alpha-2 — grouping only, never stored. */
  code: string;
  /** English short name; this is the stored value. */
  name: string;
  cities?: string[];
};

/** Shown first in the country dropdown, in this order. */
export const COMMON_COUNTRY_CODES = [
  'AE',
  'SA',
  'PK',
  'IN',
  'GB',
  'US',
  'QA',
  'KW',
  'OM',
  'BH',
  'EG',
];

/** Every ISO 3166-1 entry, alphabetical by name. */
const COUNTRY_NAMES = [
  ['AF', 'Afghanistan'],
  ['AX', 'Åland Islands'],
  ['AL', 'Albania'],
  ['DZ', 'Algeria'],
  ['AS', 'American Samoa'],
  ['AD', 'Andorra'],
  ['AO', 'Angola'],
  ['AI', 'Anguilla'],
  ['AQ', 'Antarctica'],
  ['AG', 'Antigua and Barbuda'],
  ['AR', 'Argentina'],
  ['AM', 'Armenia'],
  ['AW', 'Aruba'],
  ['AU', 'Australia'],
  ['AT', 'Austria'],
  ['AZ', 'Azerbaijan'],
  ['BS', 'Bahamas'],
  ['BH', 'Bahrain'],
  ['BD', 'Bangladesh'],
  ['BB', 'Barbados'],
  ['BY', 'Belarus'],
  ['BE', 'Belgium'],
  ['BZ', 'Belize'],
  ['BJ', 'Benin'],
  ['BM', 'Bermuda'],
  ['BT', 'Bhutan'],
  ['BO', 'Bolivia'],
  ['BQ', 'Bonaire, Sint Eustatius and Saba'],
  ['BA', 'Bosnia and Herzegovina'],
  ['BW', 'Botswana'],
  ['BV', 'Bouvet Island'],
  ['BR', 'Brazil'],
  ['IO', 'British Indian Ocean Territory'],
  ['VG', 'British Virgin Islands'],
  ['BN', 'Brunei'],
  ['BG', 'Bulgaria'],
  ['BF', 'Burkina Faso'],
  ['BI', 'Burundi'],
  ['KH', 'Cambodia'],
  ['CM', 'Cameroon'],
  ['CA', 'Canada'],
  ['CV', 'Cape Verde'],
  ['KY', 'Cayman Islands'],
  ['CF', 'Central African Republic'],
  ['TD', 'Chad'],
  ['CL', 'Chile'],
  ['CN', 'China'],
  ['CX', 'Christmas Island'],
  ['CC', 'Cocos (Keeling) Islands'],
  ['CO', 'Colombia'],
  ['KM', 'Comoros'],
  ['CG', 'Congo'],
  ['CD', 'Congo (DRC)'],
  ['CK', 'Cook Islands'],
  ['CR', 'Costa Rica'],
  ['HR', 'Croatia'],
  ['CU', 'Cuba'],
  ['CW', 'Curaçao'],
  ['CY', 'Cyprus'],
  ['CZ', 'Czechia'],
  ['DK', 'Denmark'],
  ['DJ', 'Djibouti'],
  ['DM', 'Dominica'],
  ['DO', 'Dominican Republic'],
  ['EC', 'Ecuador'],
  ['EG', 'Egypt'],
  ['SV', 'El Salvador'],
  ['GQ', 'Equatorial Guinea'],
  ['ER', 'Eritrea'],
  ['EE', 'Estonia'],
  ['SZ', 'Eswatini'],
  ['ET', 'Ethiopia'],
  ['FK', 'Falkland Islands'],
  ['FO', 'Faroe Islands'],
  ['FJ', 'Fiji'],
  ['FI', 'Finland'],
  ['FR', 'France'],
  ['GF', 'French Guiana'],
  ['PF', 'French Polynesia'],
  ['TF', 'French Southern Territories'],
  ['GA', 'Gabon'],
  ['GM', 'Gambia'],
  ['GE', 'Georgia'],
  ['DE', 'Germany'],
  ['GH', 'Ghana'],
  ['GI', 'Gibraltar'],
  ['GR', 'Greece'],
  ['GL', 'Greenland'],
  ['GD', 'Grenada'],
  ['GP', 'Guadeloupe'],
  ['GU', 'Guam'],
  ['GT', 'Guatemala'],
  ['GG', 'Guernsey'],
  ['GN', 'Guinea'],
  ['GW', 'Guinea-Bissau'],
  ['GY', 'Guyana'],
  ['HT', 'Haiti'],
  ['HM', 'Heard Island and McDonald Islands'],
  ['HN', 'Honduras'],
  ['HK', 'Hong Kong'],
  ['HU', 'Hungary'],
  ['IS', 'Iceland'],
  ['IN', 'India'],
  ['ID', 'Indonesia'],
  ['IR', 'Iran'],
  ['IQ', 'Iraq'],
  ['IE', 'Ireland'],
  ['IM', 'Isle of Man'],
  ['IL', 'Israel'],
  ['IT', 'Italy'],
  ['CI', 'Ivory Coast'],
  ['JM', 'Jamaica'],
  ['JP', 'Japan'],
  ['JE', 'Jersey'],
  ['JO', 'Jordan'],
  ['KZ', 'Kazakhstan'],
  ['KE', 'Kenya'],
  ['KI', 'Kiribati'],
  ['KW', 'Kuwait'],
  ['KG', 'Kyrgyzstan'],
  ['LA', 'Laos'],
  ['LV', 'Latvia'],
  ['LB', 'Lebanon'],
  ['LS', 'Lesotho'],
  ['LR', 'Liberia'],
  ['LY', 'Libya'],
  ['LI', 'Liechtenstein'],
  ['LT', 'Lithuania'],
  ['LU', 'Luxembourg'],
  ['MO', 'Macau'],
  ['MG', 'Madagascar'],
  ['MW', 'Malawi'],
  ['MY', 'Malaysia'],
  ['MV', 'Maldives'],
  ['ML', 'Mali'],
  ['MT', 'Malta'],
  ['MH', 'Marshall Islands'],
  ['MQ', 'Martinique'],
  ['MR', 'Mauritania'],
  ['MU', 'Mauritius'],
  ['YT', 'Mayotte'],
  ['MX', 'Mexico'],
  ['FM', 'Micronesia'],
  ['MD', 'Moldova'],
  ['MC', 'Monaco'],
  ['MN', 'Mongolia'],
  ['ME', 'Montenegro'],
  ['MS', 'Montserrat'],
  ['MA', 'Morocco'],
  ['MZ', 'Mozambique'],
  ['MM', 'Myanmar'],
  ['NA', 'Namibia'],
  ['NR', 'Nauru'],
  ['NP', 'Nepal'],
  ['NL', 'Netherlands'],
  ['NC', 'New Caledonia'],
  ['NZ', 'New Zealand'],
  ['NI', 'Nicaragua'],
  ['NE', 'Niger'],
  ['NG', 'Nigeria'],
  ['NU', 'Niue'],
  ['NF', 'Norfolk Island'],
  ['KP', 'North Korea'],
  ['MK', 'North Macedonia'],
  ['MP', 'Northern Mariana Islands'],
  ['NO', 'Norway'],
  ['OM', 'Oman'],
  ['PK', 'Pakistan'],
  ['PW', 'Palau'],
  ['PS', 'Palestine'],
  ['PA', 'Panama'],
  ['PG', 'Papua New Guinea'],
  ['PY', 'Paraguay'],
  ['PE', 'Peru'],
  ['PH', 'Philippines'],
  ['PN', 'Pitcairn Islands'],
  ['PL', 'Poland'],
  ['PT', 'Portugal'],
  ['PR', 'Puerto Rico'],
  ['QA', 'Qatar'],
  ['RE', 'Réunion'],
  ['RO', 'Romania'],
  ['RU', 'Russia'],
  ['RW', 'Rwanda'],
  ['BL', 'Saint Barthélemy'],
  ['SH', 'Saint Helena'],
  ['KN', 'Saint Kitts and Nevis'],
  ['LC', 'Saint Lucia'],
  ['MF', 'Saint Martin'],
  ['PM', 'Saint Pierre and Miquelon'],
  ['VC', 'Saint Vincent and the Grenadines'],
  ['WS', 'Samoa'],
  ['SM', 'San Marino'],
  ['ST', 'São Tomé and Príncipe'],
  ['SA', 'Saudi Arabia'],
  ['SN', 'Senegal'],
  ['RS', 'Serbia'],
  ['SC', 'Seychelles'],
  ['SL', 'Sierra Leone'],
  ['SG', 'Singapore'],
  ['SX', 'Sint Maarten'],
  ['SK', 'Slovakia'],
  ['SI', 'Slovenia'],
  ['SB', 'Solomon Islands'],
  ['SO', 'Somalia'],
  ['ZA', 'South Africa'],
  ['GS', 'South Georgia and the South Sandwich Islands'],
  ['KR', 'South Korea'],
  ['SS', 'South Sudan'],
  ['ES', 'Spain'],
  ['LK', 'Sri Lanka'],
  ['SD', 'Sudan'],
  ['SR', 'Suriname'],
  ['SJ', 'Svalbard and Jan Mayen'],
  ['SE', 'Sweden'],
  ['CH', 'Switzerland'],
  ['SY', 'Syria'],
  ['TW', 'Taiwan'],
  ['TJ', 'Tajikistan'],
  ['TZ', 'Tanzania'],
  ['TH', 'Thailand'],
  ['TL', 'Timor-Leste'],
  ['TG', 'Togo'],
  ['TK', 'Tokelau'],
  ['TO', 'Tonga'],
  ['TT', 'Trinidad and Tobago'],
  ['TN', 'Tunisia'],
  ['TR', 'Turkey'],
  ['TM', 'Turkmenistan'],
  ['TC', 'Turks and Caicos Islands'],
  ['TV', 'Tuvalu'],
  ['UG', 'Uganda'],
  ['UA', 'Ukraine'],
  ['AE', 'United Arab Emirates'],
  ['GB', 'United Kingdom'],
  ['US', 'United States'],
  ['UM', 'United States Minor Outlying Islands'],
  ['VI', 'United States Virgin Islands'],
  ['UY', 'Uruguay'],
  ['UZ', 'Uzbekistan'],
  ['VU', 'Vanuatu'],
  ['VA', 'Vatican City'],
  ['VE', 'Venezuela'],
  ['VN', 'Vietnam'],
  ['WF', 'Wallis and Futuna'],
  ['EH', 'Western Sahara'],
  ['YE', 'Yemen'],
  ['ZM', 'Zambia'],
  ['ZW', 'Zimbabwe'],
] as const;

type CountryCode = (typeof COUNTRY_NAMES)[number][0];

// prettier-ignore
const CITIES: Partial<Record<CountryCode, string[]>> = {
  // Afghanistan
  AF: [
    'Asadabad', 'Aybak', 'Bamyan', 'Charikar', 'Faizabad', 'Farah', 'Gardez', 'Ghazni', 'Herat',
    'Jalalabad', 'Kabul', 'Kandahar', 'Khost', 'Kunduz', 'Lashkar Gah', 'Maymana', 'Mazar-i-Sharif',
    'Mehtar Lam', 'Puli Khumri', 'Qalat', 'Sar-e Pol', 'Sheberghan', 'Taloqan', 'Zaranj',
  ],
  // Australia
  AU: [
    'Adelaide', 'Albury', 'Alice Springs', 'Ballarat', 'Bendigo', 'Brisbane', 'Bunbury',
    'Bundaberg', 'Cairns', 'Canberra', 'Coffs Harbour', 'Darwin', 'Dubbo', 'Fremantle', 'Geelong',
    'Geraldton', 'Gladstone', 'Gold Coast', 'Hervey Bay', 'Hobart', 'Launceston', 'Mackay',
    'Melbourne', 'Mildura', 'Newcastle', 'Orange', 'Parramatta', 'Perth', 'Rockhampton',
    'Shepparton', 'Sunshine Coast', 'Sydney', 'Tamworth', 'Toowoomba', 'Townsville', 'Wagga Wagga',
    'Wollongong',
  ],
  // Azerbaijan
  AZ: [
    'Agdam', 'Astara', 'Baku', 'Barda', 'Gabala', 'Ganja', 'Imishli', 'Khankendi', 'Khirdalan',
    'Lankaran', 'Mingachevir', 'Nakhchivan', 'Quba', 'Qusar', 'Salyan', 'Shaki', 'Shamakhi',
    'Shirvan', 'Shusha', 'Sumqayit', 'Yevlakh', 'Zaqatala',
  ],
  // Bahrain
  BH: [
    'Al Hidd', 'Amwaj Islands', 'Arad', 'Askar', 'Budaiya', 'Busaiteen', 'Hamad Town', 'Isa Town',
    'Jidhafs', 'Juffair', 'Manama', 'Muharraq', 'Riffa', 'Saar', 'Sanabis', 'Seef', 'Sitra',
    'Tubli', 'Zallaq',
  ],
  // Bangladesh
  BD: [
    'Barisal', 'Bogra', 'Brahmanbaria', 'Chittagong', 'Comilla', "Cox's Bazar", 'Dhaka', 'Dinajpur',
    'Faridpur', 'Feni', 'Gazipur', 'Jessore', 'Khulna', 'Kushtia', 'Mymensingh', 'Narayanganj',
    'Narsingdi', 'Noakhali', 'Pabna', 'Rajshahi', 'Rangpur', 'Savar', 'Sylhet', 'Tangail',
  ],
  // Canada
  CA: [
    'Abbotsford', 'Barrie', 'Brampton', 'Burlington', 'Burnaby', 'Calgary', 'Charlottetown',
    'Edmonton', 'Fredericton', 'Gatineau', 'Guelph', 'Halifax', 'Hamilton', 'Kamloops', 'Kelowna',
    'Kingston', 'Kitchener', 'Laval', 'Lethbridge', 'London', 'Longueuil', 'Markham', 'Mississauga',
    'Moncton', 'Montreal', 'Nanaimo', 'Oakville', 'Oshawa', 'Ottawa', 'Quebec City', 'Red Deer',
    'Regina', 'Richmond', 'Richmond Hill', 'Saskatoon', 'Sherbrooke', "St. John's", 'Sudbury',
    'Surrey', 'Thunder Bay', 'Toronto', 'Vancouver', 'Vaughan', 'Victoria', 'Waterloo', 'Windsor',
    'Winnipeg',
  ],
  // China
  CN: [
    'Baoding', 'Beijing', 'Changchun', 'Changsha', 'Changzhou', 'Chengdu', 'Chongqing', 'Dalian',
    'Dongguan', 'Foshan', 'Fuzhou', 'Guangzhou', 'Guilin', 'Guiyang', 'Haikou', 'Hangzhou',
    'Harbin', 'Hefei', 'Hohhot', 'Huizhou', 'Jiaxing', 'Jinan', 'Kunming', 'Lanzhou', 'Lhasa',
    'Nanchang', 'Nanjing', 'Nanning', 'Nantong', 'Ningbo', 'Qingdao', 'Quanzhou', 'Sanya',
    'Shanghai', 'Shantou', 'Shenyang', 'Shenzhen', 'Shijiazhuang', 'Suzhou', 'Taiyuan', 'Tangshan',
    'Tianjin', 'Urumqi', 'Weihai', 'Wenzhou', 'Wuhan', 'Wuxi', "Xi'an", 'Xiamen', 'Xining',
    'Xuzhou', 'Yantai', 'Yinchuan', 'Yiwu', 'Zhengzhou', 'Zhongshan', 'Zhuhai', 'Zibo',
  ],
  // Egypt
  EG: [
    '10th of Ramadan City', '6th of October City', 'Alexandria', 'Arish', 'Aswan', 'Asyut', 'Banha',
    'Beni Suef', 'Cairo', 'Damanhur', 'Damietta', 'El Mahalla El Kubra', 'Faiyum', 'Giza',
    'Hurghada', 'Ismailia', 'Kafr El Sheikh', 'Luxor', 'Mansoura', 'Marsa Matruh', 'Minya',
    'New Administrative Capital', 'New Cairo', 'Port Said', 'Qena', 'Sharm El Sheikh',
    'Shubra El Kheima', 'Sohag', 'Suez', 'Tanta', 'Zagazig',
  ],
  // France
  FR: [
    'Aix-en-Provence', 'Amiens', 'Angers', 'Annecy', 'Avignon', 'Bayonne', 'Besançon', 'Biarritz',
    'Bordeaux', 'Brest', 'Caen', 'Calais', 'Cannes', 'Clermont-Ferrand', 'Dijon', 'Grenoble',
    'La Rochelle', 'Le Havre', 'Le Mans', 'Lille', 'Limoges', 'Lyon', 'Marseille', 'Metz',
    'Montpellier', 'Mulhouse', 'Nancy', 'Nantes', 'Nice', 'Nîmes', 'Orléans', 'Paris', 'Pau',
    'Perpignan', 'Reims', 'Rennes', 'Rouen', 'Saint-Étienne', 'Strasbourg', 'Toulon', 'Toulouse',
    'Tours', 'Versailles', 'Villeurbanne',
  ],
  // Georgia
  GE: [
    'Akhaltsikhe', 'Batumi', 'Bolnisi', 'Borjomi', 'Chiatura', 'Gori', 'Gudauri', 'Kaspi',
    'Khashuri', 'Kobuleti', 'Kutaisi', 'Marneuli', 'Mtskheta', 'Ozurgeti', 'Poti', 'Rustavi',
    'Sagarejo', 'Samtredia', 'Senaki', 'Tbilisi', 'Telavi', 'Tsqaltubo', 'Zestafoni', 'Zugdidi',
  ],
  // Germany
  DE: [
    'Aachen', 'Augsburg', 'Berlin', 'Bielefeld', 'Bochum', 'Bonn', 'Braunschweig', 'Bremen',
    'Chemnitz', 'Cologne', 'Darmstadt', 'Dortmund', 'Dresden', 'Duisburg', 'Düsseldorf', 'Erfurt',
    'Essen', 'Frankfurt', 'Freiburg', 'Gelsenkirchen', 'Göttingen', 'Halle', 'Hamburg', 'Hanover',
    'Heidelberg', 'Ingolstadt', 'Karlsruhe', 'Kassel', 'Kiel', 'Krefeld', 'Leipzig', 'Lübeck',
    'Magdeburg', 'Mainz', 'Mannheim', 'Mönchengladbach', 'Munich', 'Münster', 'Nuremberg',
    'Oldenburg', 'Potsdam', 'Regensburg', 'Rostock', 'Saarbrücken', 'Stuttgart', 'Ulm', 'Wiesbaden',
    'Wolfsburg', 'Wuppertal', 'Würzburg',
  ],
  // Hong Kong
  HK: [
    'Aberdeen', 'Admiralty', 'Causeway Bay', 'Central', 'Discovery Bay', 'Hong Kong', 'Hung Hom',
    'Kowloon', 'Kwai Chung', 'Kwun Tong', 'Lantau Island', 'Ma On Shan', 'Mong Kok', 'North Point',
    'Quarry Bay', 'Sai Kung', 'Sha Tin', 'Sheung Wan', 'Tai Po', 'Tseung Kwan O', 'Tsim Sha Tsui',
    'Tsuen Wan', 'Tuen Mun', 'Tung Chung', 'Wan Chai', 'Yuen Long',
  ],
  // India
  IN: [
    'Agra', 'Ahmedabad', 'Ajmer', 'Aligarh', 'Amravati', 'Amritsar', 'Aurangabad', 'Bareilly',
    'Bathinda', 'Belagavi', 'Bengaluru', 'Bhilai', 'Bhiwandi', 'Bhopal', 'Bhubaneswar', 'Bikaner',
    'Chandigarh', 'Chennai', 'Coimbatore', 'Cuttack', 'Dehradun', 'Delhi', 'Dhanbad', 'Durgapur',
    'Faridabad', 'Firozabad', 'Ghaziabad', 'Gorakhpur', 'Greater Noida', 'Guntur', 'Gurugram',
    'Guwahati', 'Gwalior', 'Howrah', 'Hubli', 'Hyderabad', 'Indore', 'Jabalpur', 'Jaipur',
    'Jalandhar', 'Jalgaon', 'Jammu', 'Jamshedpur', 'Jodhpur', 'Kakinada', 'Kanpur', 'Kochi',
    'Kolhapur', 'Kolkata', 'Kollam', 'Kota', 'Kozhikode', 'Lucknow', 'Ludhiana', 'Madurai',
    'Mangaluru', 'Meerut', 'Mohali', 'Moradabad', 'Mumbai', 'Mysuru', 'Nagpur', 'Nanded', 'Nashik',
    'Navi Mumbai', 'Nellore', 'New Delhi', 'Noida', 'Panaji', 'Patiala', 'Patna', 'Pondicherry',
    'Prayagraj', 'Pune', 'Raipur', 'Rajkot', 'Ranchi', 'Rourkela', 'Saharanpur', 'Salem', 'Sangli',
    'Shimla', 'Siliguri', 'Solapur', 'Srinagar', 'Surat', 'Thane', 'Thiruvananthapuram', 'Thrissur',
    'Tiruchirappalli', 'Tirupati', 'Tiruppur', 'Udaipur', 'Ujjain', 'Vadodara', 'Varanasi',
    'Vellore', 'Vijayawada', 'Visakhapatnam', 'Warangal',
  ],
  // Indonesia
  ID: [
    'Ambon', 'Balikpapan', 'Banda Aceh', 'Bandar Lampung', 'Bandung', 'Banjarmasin', 'Batam',
    'Bekasi', 'Bengkulu', 'Bogor', 'Cimahi', 'Cirebon', 'Denpasar', 'Depok', 'Gorontalo', 'Jakarta',
    'Jambi', 'Jayapura', 'Kediri', 'Kendari', 'Kupang', 'Makassar', 'Malang', 'Manado', 'Mataram',
    'Medan', 'Padang', 'Palembang', 'Palu', 'Pangkal Pinang', 'Pekalongan', 'Pekanbaru',
    'Pontianak', 'Samarinda', 'Semarang', 'Serang', 'South Tangerang', 'Surabaya', 'Surakarta',
    'Tangerang', 'Tasikmalaya', 'Tegal', 'Yogyakarta',
  ],
  // Iran
  IR: [
    'Abadan', 'Ahvaz', 'Arak', 'Ardabil', 'Bandar Abbas', 'Birjand', 'Bojnurd', 'Bushehr',
    'Chabahar', 'Dezful', 'Eslamshahr', 'Gorgan', 'Hamadan', 'Ilam', 'Isfahan', 'Karaj', 'Kashan',
    'Kerman', 'Kermanshah', 'Khorramabad', 'Kish', 'Mashhad', 'Najafabad', 'Qazvin', 'Qeshm', 'Qom',
    'Rasht', 'Sanandaj', 'Sari', 'Semnan', 'Shiraz', 'Tabriz', 'Tehran', 'Urmia', 'Yazd', 'Zahedan',
    'Zanjan',
  ],
  // Iraq
  IQ: [
    'Amarah', 'Baghdad', 'Baqubah', 'Basra', 'Diwaniyah', 'Duhok', 'Erbil', 'Fallujah', 'Halabja',
    'Hillah', 'Karbala', 'Kirkuk', 'Kut', 'Mosul', 'Najaf', 'Nasiriyah', 'Ramadi', 'Samarra',
    'Sulaymaniyah', 'Tikrit', 'Umm Qasr', 'Zakho', 'Zubair',
  ],
  // Ireland
  IE: [
    'Athlone', 'Bray', 'Carlow', 'Celbridge', 'Clonmel', 'Cork', 'Drogheda', 'Dublin', 'Dundalk',
    'Ennis', 'Galway', 'Kilkenny', 'Letterkenny', 'Limerick', 'Mullingar', 'Naas', 'Navan',
    'Newbridge', 'Portlaoise', 'Sligo', 'Swords', 'Tralee', 'Waterford', 'Wexford',
  ],
  // Italy
  IT: [
    'Ancona', 'Bari', 'Bergamo', 'Bologna', 'Bolzano', 'Brescia', 'Cagliari', 'Catania',
    'Catanzaro', 'Como', 'Ferrara', 'Florence', 'Foggia', 'Genoa', 'La Spezia', 'Lecce', 'Livorno',
    'Lucca', 'Messina', 'Milan', 'Modena', 'Monza', 'Naples', 'Novara', 'Padua', 'Palermo', 'Parma',
    'Perugia', 'Pescara', 'Piacenza', 'Pisa', 'Prato', 'Ravenna', 'Reggio Calabria',
    'Reggio Emilia', 'Rimini', 'Rome', 'Salerno', 'Sassari', 'Siena', 'Taranto', 'Trento',
    'Treviso', 'Trieste', 'Turin', 'Udine', 'Varese', 'Venice', 'Verona', 'Vicenza',
  ],
  // Japan
  JP: [
    'Akita', 'Aomori', 'Chiba', 'Fujisawa', 'Fukuoka', 'Fukuyama', 'Funabashi', 'Gifu', 'Hachioji',
    'Hamamatsu', 'Himeji', 'Hiroshima', 'Kagoshima', 'Kanazawa', 'Kawaguchi', 'Kawasaki',
    'Kitakyushu', 'Kobe', 'Kochi', 'Kumamoto', 'Kurashiki', 'Kyoto', 'Matsudo', 'Matsuyama',
    'Morioka', 'Nagano', 'Nagasaki', 'Nagoya', 'Naha', 'Nara', 'Niigata', 'Nishinomiya', 'Oita',
    'Okayama', 'Osaka', 'Sagamihara', 'Saitama', 'Sakai', 'Sapporo', 'Sendai', 'Shizuoka',
    'Takamatsu', 'Tokyo', 'Toyama', 'Toyota', 'Tsukuba', 'Utsunomiya', 'Wakayama', 'Yokohama',
    'Yokosuka',
  ],
  // Jordan
  JO: [
    'Ajloun', 'Amman', 'Aqaba', 'Fuheis', 'Irbid', 'Jerash', 'Karak', "Ma'an", 'Madaba', 'Mafraq',
    'Ramtha', 'Russeifa', 'Sahab', 'Salt', 'Tafilah', 'Zarqa',
  ],
  // Kazakhstan
  KZ: [
    'Aktau', 'Aktobe', 'Almaty', 'Astana', 'Atyrau', 'Balkhash', 'Ekibastuz', 'Karaganda',
    'Kokshetau', 'Kostanay', 'Kyzylorda', 'Oral', 'Oskemen', 'Pavlodar', 'Petropavl', 'Rudny',
    'Semey', 'Shymkent', 'Taldykorgan', 'Taraz', 'Temirtau', 'Turkistan', 'Zhanaozen', 'Zhezkazgan',
  ],
  // Kenya
  KE: [
    'Bungoma', 'Diani', 'Eldoret', 'Embu', 'Garissa', 'Isiolo', 'Kakamega', 'Kericho', 'Kiambu',
    'Kisii', 'Kisumu', 'Kitale', 'Lamu', 'Machakos', 'Malindi', 'Meru', 'Mombasa', 'Nairobi',
    'Naivasha', 'Nakuru', 'Nanyuki', 'Nyeri', 'Ruiru', 'Thika',
  ],
  // Kuwait
  KW: [
    'Abu Halifa', 'Ahmadi', 'Bayan', 'Fahaheel', 'Farwaniya', 'Fintas', 'Hawalli', 'Jabriya',
    'Jahra', 'Jleeb Al Shuyoukh', 'Khaitan', 'Kuwait City', 'Mahboula', 'Mangaf', 'Mishref',
    'Mubarak Al Kabeer', 'Sabah Al Salem', 'Salmiya', 'Salwa', 'Shuwaikh',
  ],
  // Lebanon
  LB: [
    'Aley', 'Antelias', 'Baalbek', 'Batroun', 'Beirut', 'Beit Mery', 'Bint Jbeil', 'Broummana',
    'Byblos', 'Chtaura', 'Dbayeh', 'Halba', 'Hermel', 'Jounieh', 'Nabatieh', 'Sidon', 'Tripoli',
    'Tyre', 'Zahle', 'Zgharta',
  ],
  // Malaysia
  MY: [
    'Alor Setar', 'Ampang', 'Batu Pahat', 'Bintulu', 'Cyberjaya', 'George Town', 'Ipoh',
    'Iskandar Puteri', 'Johor Bahru', 'Kajang', 'Klang', 'Kluang', 'Kota Bharu', 'Kota Kinabalu',
    'Kuala Lumpur', 'Kuala Terengganu', 'Kuantan', 'Kuching', 'Langkawi', 'Malacca', 'Miri',
    'Petaling Jaya', 'Putrajaya', 'Sandakan', 'Seremban', 'Seri Kembangan', 'Shah Alam', 'Sibu',
    'Subang Jaya', 'Sungai Petani', 'Taiping', 'Tawau',
  ],
  // Morocco
  MA: [
    'Agadir', 'Beni Mellal', 'Berrechid', 'Casablanca', 'Dakhla', 'El Jadida', 'Errachidia',
    'Essaouira', 'Fez', 'Kenitra', 'Khouribga', 'Laayoune', 'Marrakesh', 'Meknes', 'Mohammedia',
    'Nador', 'Ouarzazate', 'Oujda', 'Rabat', 'Safi', 'Sale', 'Settat', 'Tangier', 'Tetouan',
  ],
  // Nepal
  NP: [
    'Bhairahawa', 'Bhaktapur', 'Bharatpur', 'Biratnagar', 'Birgunj', 'Butwal', 'Dhangadhi',
    'Dharan', 'Ghorahi', 'Gorkha', 'Hetauda', 'Itahari', 'Janakpur', 'Kathmandu', 'Kirtipur',
    'Lalitpur', 'Mahendranagar', 'Nepalgunj', 'Pokhara', 'Tulsipur',
  ],
  // Netherlands
  NL: [
    'Alkmaar', 'Almere', 'Amersfoort', 'Amsterdam', 'Apeldoorn', 'Arnhem', 'Breda', 'Delft',
    'Den Bosch', 'Deventer', 'Dordrecht', 'Eindhoven', 'Enschede', 'Groningen', 'Haarlem',
    'Heerlen', 'Hilversum', 'Hoofddorp', 'Leeuwarden', 'Leiden', 'Maastricht', 'Nijmegen',
    'Rotterdam', 'The Hague', 'Tilburg', 'Utrecht', 'Venlo', 'Zaanstad', 'Zoetermeer', 'Zwolle',
  ],
  // Nigeria
  NG: [
    'Aba', 'Abeokuta', 'Abuja', 'Ado Ekiti', 'Akure', 'Asaba', 'Awka', 'Bauchi', 'Benin City',
    'Calabar', 'Enugu', 'Gombe', 'Ibadan', 'Ikeja', 'Ilorin', 'Jalingo', 'Jos', 'Kaduna', 'Kano',
    'Katsina', 'Lafia', 'Lagos', 'Lekki', 'Lokoja', 'Maiduguri', 'Makurdi', 'Minna', 'Onitsha',
    'Osogbo', 'Owerri', 'Port Harcourt', 'Sokoto', 'Umuahia', 'Uyo', 'Warri', 'Yola', 'Zaria',
  ],
  // Oman
  OM: [
    'Adam', 'Al Khoud', 'Bahla', 'Barka', 'Bausher', 'Bidbid', 'Buraimi', 'Duqm', 'Haima', 'Ibra',
    'Ibri', 'Khasab', 'Mabela', 'Muscat', 'Muttrah', 'Nizwa', 'Rustaq', 'Ruwi', 'Saham', 'Salalah',
    'Seeb', 'Shinas', 'Sohar', 'Sumail', 'Sur',
  ],
  // Pakistan
  PK: [
    'Abbottabad', 'Arifwala', 'Attock', 'Badin', 'Bahawalnagar', 'Bahawalpur', 'Bannu', 'Bhakkar',
    'Burewala', 'Chakwal', 'Chaman', 'Charsadda', 'Chiniot', 'Chishtian', 'Chitral', 'Dadu',
    'Daska', 'Dera Ghazi Khan', 'Dera Ismail Khan', 'Faisalabad', 'Ghotki', 'Gilgit', 'Gojra',
    'Gujar Khan', 'Gujranwala', 'Gujrat', 'Gwadar', 'Hafizabad', 'Haripur', 'Hasilpur', 'Hunza',
    'Hyderabad', 'Islamabad', 'Jacobabad', 'Jamshoro', 'Jaranwala', 'Jhang', 'Jhelum', 'Kamoke',
    'Kandhkot', 'Karachi', 'Kasur', 'Khairpur', 'Khanewal', 'Kharian', 'Khuzdar', 'Kohat',
    'Kot Addu', 'Kotli', 'Lahore', 'Larkana', 'Layyah', 'Lodhran', 'Mandi Bahauddin', 'Mansehra',
    'Mardan', 'Mianwali', 'Mingora', 'Mirpur', 'Mirpur Khas', 'Multan', 'Muridke', 'Murree',
    'Muzaffarabad', 'Muzaffargarh', 'Narowal', 'Nawabshah', 'Nowshera', 'Okara', 'Pakpattan',
    'Pattoki', 'Peshawar', 'Quetta', 'Rahim Yar Khan', 'Rawalpindi', 'Sadiqabad', 'Sahiwal',
    'Samundri', 'Sargodha', 'Sheikhupura', 'Shikarpur', 'Sialkot', 'Sibi', 'Skardu', 'Sukkur',
    'Swabi', 'Tando Adam', 'Tando Allahyar', 'Taxila', 'Thatta', 'Timergara', 'Toba Tek Singh',
    'Turbat', 'Vehari', 'Wah Cantt', 'Wazirabad', 'Zhob',
  ],
  // Philippines
  PH: [
    'Angeles City', 'Antipolo', 'Bacolod', 'Bacoor', 'Baguio', 'Balanga', 'Batangas City', 'Biñan',
    'Butuan', 'Cabanatuan', 'Cagayan de Oro', 'Calamba', 'Caloocan', 'Cavite City', 'Cebu City',
    'Cotabato City', 'Dasmariñas', 'Davao City', 'Dumaguete', 'General Santos', 'Iligan',
    'Iloilo City', 'Imus', 'Laoag', 'Lapu-Lapu City', 'Las Piñas', 'Legazpi', 'Lipa', 'Makati',
    'Malolos', 'Mandaluyong', 'Mandaue', 'Manila', 'Marikina', 'Muntinlupa', 'Naga', 'Olongapo',
    'Ormoc', 'Parañaque', 'Pasay', 'Pasig', 'Puerto Princesa', 'Quezon City', 'San Fernando',
    'San Jose del Monte', 'Santa Rosa', 'Tacloban', 'Tagbilaran', 'Taguig', 'Tarlac City',
    'Valenzuela', 'Vigan', 'Zamboanga City',
  ],
  // Qatar
  QA: [
    'Al Daayen', 'Al Khor', 'Al Rayyan', 'Al Ruwais', 'Al Shahaniya', 'Al Shamal', 'Al Wakrah',
    'Al Wukair', 'Doha', 'Dukhan', 'Lusail', 'Mesaieed', 'Ras Laffan', 'Umm Salal',
  ],
  // Russia
  RU: [
    'Arkhangelsk', 'Astrakhan', 'Barnaul', 'Chelyabinsk', 'Grozny', 'Irkutsk', 'Izhevsk',
    'Kaliningrad', 'Kazan', 'Kemerovo', 'Khabarovsk', 'Kirov', 'Krasnodar', 'Krasnoyarsk',
    'Lipetsk', 'Makhachkala', 'Moscow', 'Murmansk', 'Naberezhnye Chelny', 'Nizhny Novgorod',
    'Novokuznetsk', 'Novosibirsk', 'Omsk', 'Orenburg', 'Penza', 'Perm', 'Rostov-on-Don', 'Ryazan',
    'Saint Petersburg', 'Samara', 'Saratov', 'Sochi', 'Surgut', 'Tolyatti', 'Tomsk', 'Tula',
    'Tyumen', 'Ufa', 'Ulyanovsk', 'Vladikavkaz', 'Vladivostok', 'Volgograd', 'Voronezh',
    'Yaroslavl', 'Yekaterinburg',
  ],
  // Saudi Arabia
  SA: [
    'Abha', 'Al Ahsa', 'Al Bahah', 'Al Khafji', 'Al Kharj', 'Al Majmaah', 'Al Mubarraz', 'Al Zulfi',
    'AlUla', 'Arar', 'Bisha', 'Buraidah', 'Dammam', 'Dawadmi', 'Dhahran', 'Hafar Al Batin', 'Hail',
    'Hofuf', 'Jeddah', 'Jizan', 'Jubail', 'Khamis Mushait', 'Khobar', 'King Abdullah Economic City',
    'Mecca', 'Medina', 'Najran', 'Qatif', 'Qurayyat', 'Rabigh', 'Ras Tanura', 'Riyadh', 'Sakaka',
    'Tabuk', 'Taif', 'Unaizah', 'Yanbu',
  ],
  // Singapore
  SG: [
    'Ang Mo Kio', 'Bedok', 'Bishan', 'Bukit Timah', 'Changi', 'Clementi', 'Hougang', 'Jurong',
    'Marina Bay', 'Orchard', 'Pasir Ris', 'Punggol', 'Queenstown', 'Sengkang', 'Serangoon',
    'Singapore', 'Tampines', 'Toa Payoh', 'Woodlands', 'Yishun',
  ],
  // South Africa
  ZA: [
    'Benoni', 'Bloemfontein', 'Boksburg', 'Cape Town', 'Centurion', 'Durban', 'East London',
    'George', 'Germiston', 'Johannesburg', 'Kimberley', 'Midrand', 'Nelspruit', 'Paarl',
    'Pietermaritzburg', 'Polokwane', 'Port Elizabeth', 'Potchefstroom', 'Pretoria', 'Randburg',
    'Richards Bay', 'Roodepoort', 'Rustenburg', 'Sandton', 'Soweto', 'Stellenbosch', 'Umhlanga',
    'Upington', 'Vereeniging', 'Welkom', 'Witbank',
  ],
  // South Korea
  KR: [
    'Ansan', 'Anyang', 'Bucheon', 'Busan', 'Changwon', 'Cheonan', 'Cheongju', 'Chuncheon', 'Daegu',
    'Daejeon', 'Gangneung', 'Gimhae', 'Goyang', 'Gumi', 'Gwangju', 'Gyeongju', 'Hwaseong',
    'Incheon', 'Jeju', 'Jeonju', 'Mokpo', 'Namyangju', 'Paju', 'Pohang', 'Pyeongtaek', 'Sejong',
    'Seongnam', 'Seoul', 'Siheung', 'Suncheon', 'Suwon', 'Uijeongbu', 'Ulsan', 'Wonju', 'Yeosu',
    'Yongin',
  ],
  // Spain
  ES: [
    'A Coruña', 'Albacete', 'Alcalá de Henares', 'Alicante', 'Almería', 'Badalona', 'Barcelona',
    'Benidorm', 'Bilbao', 'Burgos', 'Cádiz', 'Cartagena', 'Castellón de la Plana', 'Córdoba',
    'Elche', 'Gijón', 'Girona', 'Granada', 'Ibiza', 'Jerez de la Frontera', 'Las Palmas', 'Logroño',
    'Madrid', 'Málaga', 'Marbella', 'Móstoles', 'Murcia', 'Oviedo', 'Palma', 'Pamplona', 'Sabadell',
    'Salamanca', 'San Sebastián', 'Santa Cruz de Tenerife', 'Santander', 'Seville', 'Tarragona',
    'Terrassa', 'Toledo', 'Valencia', 'Valladolid', 'Vigo', 'Vitoria-Gasteiz', 'Zaragoza',
  ],
  // Sri Lanka
  LK: [
    'Anuradhapura', 'Badulla', 'Batticaloa', 'Colombo', 'Dehiwala-Mount Lavinia', 'Galle',
    'Gampaha', 'Hambantota', 'Jaffna', 'Kalmunai', 'Kalutara', 'Kandy', 'Kurunegala', 'Matara',
    'Moratuwa', 'Negombo', 'Nuwara Eliya', 'Polonnaruwa', 'Puttalam', 'Ratnapura',
    'Sri Jayawardenepura Kotte', 'Trincomalee', 'Vavuniya',
  ],
  // Sweden
  SE: [
    'Borås', 'Eskilstuna', 'Gävle', 'Gothenburg', 'Halmstad', 'Helsingborg', 'Jönköping', 'Kalmar',
    'Karlstad', 'Kristianstad', 'Linköping', 'Luleå', 'Lund', 'Malmö', 'Norrköping', 'Örebro',
    'Östersund', 'Södertälje', 'Solna', 'Stockholm', 'Sundsvall', 'Trollhättan', 'Umeå', 'Uppsala',
    'Västerås', 'Växjö',
  ],
  // Switzerland
  CH: [
    'Aarau', 'Baden', 'Basel', 'Bern', 'Biel', 'Chur', 'Davos', 'Fribourg', 'Geneva', 'Interlaken',
    'Köniz', 'La Chaux-de-Fonds', 'Lausanne', 'Locarno', 'Lucerne', 'Lugano', 'Montreux',
    'Neuchâtel', 'Nyon', 'Schaffhausen', 'Sion', 'St. Gallen', 'Thun', 'Uster', 'Vernier', 'Vevey',
    'Winterthur', 'Zermatt', 'Zug', 'Zurich',
  ],
  // Thailand
  TH: [
    'Ayutthaya', 'Bangkok', 'Chiang Mai', 'Chiang Rai', 'Chonburi', 'Hat Yai', 'Hua Hin',
    'Kanchanaburi', 'Khon Kaen', 'Koh Samui', 'Krabi', 'Laem Chabang', 'Lampang', 'Nakhon Pathom',
    'Nakhon Ratchasima', 'Nakhon Sawan', 'Nakhon Si Thammarat', 'Nonthaburi', 'Pak Kret', 'Pattaya',
    'Phitsanulok', 'Phuket', 'Rayong', 'Sakon Nakhon', 'Samut Prakan', 'Si Racha', 'Songkhla',
    'Surat Thani', 'Trang', 'Ubon Ratchathani', 'Udon Thani',
  ],
  // Turkey
  TR: [
    'Adana', 'Alanya', 'Ankara', 'Antakya', 'Antalya', 'Aydin', 'Balikesir', 'Batman', 'Bodrum',
    'Bursa', 'Denizli', 'Diyarbakir', 'Elazig', 'Erzurum', 'Eskisehir', 'Fethiye', 'Gaziantep',
    'Istanbul', 'Izmir', 'Izmit', 'Kahramanmaras', 'Kayseri', 'Konya', 'Kusadasi', 'Malatya',
    'Manisa', 'Mardin', 'Marmaris', 'Mersin', 'Mugla', 'Rize', 'Sakarya', 'Samsun', 'Sanliurfa',
    'Sivas', 'Tekirdag', 'Trabzon', 'Van',
  ],
  // United Arab Emirates
  AE: [
    'Abu Dhabi', 'Ajman', 'Al Ain', 'Dhaid', 'Dibba', 'Dubai', 'Fujairah', 'Ghayathi', 'Hatta',
    'Jebel Ali', 'Kalba', 'Khor Fakkan', 'Liwa', 'Madinat Zayed', 'Masafi', 'Ras Al Khaimah',
    'Ruwais', 'Sharjah', 'Umm Al Quwain',
  ],
  // United Kingdom
  GB: [
    'Aberdeen', 'Basingstoke', 'Bath', 'Belfast', 'Birmingham', 'Blackpool', 'Bolton',
    'Bournemouth', 'Bradford', 'Brighton', 'Bristol', 'Cambridge', 'Canterbury', 'Cardiff',
    'Chelmsford', 'Cheltenham', 'Coventry', 'Croydon', 'Derby', 'Derry', 'Doncaster', 'Dundee',
    'Edinburgh', 'Exeter', 'Glasgow', 'Gloucester', 'Guildford', 'Huddersfield', 'Hull',
    'Inverness', 'Ipswich', 'Leeds', 'Leicester', 'Lincoln', 'Liverpool', 'London', 'Luton',
    'Manchester', 'Middlesbrough', 'Milton Keynes', 'Newcastle upon Tyne', 'Newport', 'Northampton',
    'Norwich', 'Nottingham', 'Oxford', 'Peterborough', 'Plymouth', 'Portsmouth', 'Preston',
    'Reading', 'Sheffield', 'Slough', 'Southampton', 'Stockport', 'Stoke-on-Trent', 'Sunderland',
    'Swansea', 'Swindon', 'Telford', 'Wakefield', 'Warrington', 'Watford', 'Wigan', 'Wolverhampton',
    'Worcester', 'York',
  ],
  // United States
  US: [
    'Albuquerque', 'Anaheim', 'Anchorage', 'Arlington', 'Atlanta', 'Aurora', 'Austin',
    'Bakersfield', 'Baltimore', 'Baton Rouge', 'Bellevue', 'Birmingham', 'Boise', 'Boston',
    'Buffalo', 'Chandler', 'Charleston', 'Charlotte', 'Chesapeake', 'Chicago', 'Cincinnati',
    'Cleveland', 'Colorado Springs', 'Columbus', 'Corpus Christi', 'Dallas', 'Denver', 'Des Moines',
    'Detroit', 'Durham', 'El Paso', 'Fort Lauderdale', 'Fort Worth', 'Fremont', 'Fresno',
    'Grand Rapids', 'Greensboro', 'Hartford', 'Henderson', 'Honolulu', 'Houston', 'Indianapolis',
    'Irvine', 'Irving', 'Jacksonville', 'Jersey City', 'Kansas City', 'Las Vegas', 'Lexington',
    'Little Rock', 'Long Beach', 'Los Angeles', 'Louisville', 'Madison', 'Memphis', 'Mesa', 'Miami',
    'Milwaukee', 'Minneapolis', 'Mountain View', 'Nashville', 'New Orleans', 'New York', 'Newark',
    'Norfolk', 'Oakland', 'Oklahoma City', 'Omaha', 'Orlando', 'Palo Alto', 'Philadelphia',
    'Phoenix', 'Pittsburgh', 'Plano', 'Portland', 'Providence', 'Raleigh', 'Redmond', 'Reno',
    'Richmond', 'Riverside', 'Rochester', 'Sacramento', 'Salt Lake City', 'San Antonio',
    'San Diego', 'San Francisco', 'San Jose', 'Santa Ana', 'Savannah', 'Scottsdale', 'Seattle',
    'Spokane', 'St. Louis', 'Stockton', 'Sunnyvale', 'Tacoma', 'Tampa', 'Tucson', 'Tulsa',
    'Virginia Beach', 'Washington, D.C.', 'Wichita', 'Winston-Salem',
  ],
  // Uzbekistan
  UZ: [
    'Almalyk', 'Andijan', 'Angren', 'Bekabad', 'Bukhara', 'Chirchiq', 'Denau', 'Fergana',
    'Gulistan', 'Jizzakh', 'Khiva', 'Kokand', 'Margilan', 'Namangan', 'Navoi', 'Nukus', 'Qarshi',
    'Samarkand', 'Shahrisabz', 'Tashkent', 'Termez', 'Urgench', 'Zarafshan',
  ],
};

export const COUNTRIES: readonly Country[] = COUNTRY_NAMES.map(([code, name]) => {
  const cities = CITIES[code];
  return cities ? { code, name, cities } : { code, name };
});

const BY_LOWER_NAME = new Map(COUNTRIES.map((country) => [country.name.toLowerCase(), country]));

/** Cities for a country name (case-insensitive); empty when we have no list. */
export function citiesFor(countryName: string): string[] {
  return BY_LOWER_NAME.get(countryName.trim().toLowerCase())?.cities ?? [];
}
